import { create } from 'zustand'
import axios from 'axios'

const API_URL         = import.meta.env.VITE_API_URL         || 'http://localhost:8000'
const JWT_STORAGE_KEY = import.meta.env.VITE_JWT_STORAGE_KEY || 'auth_token'
const USER_STORAGE_KEY = 'auth_user'
// ─── token helpers ────────────────────────────────────────────────────────────
// Respect the remember-me flag when choosing which store to read first.
// 'wq_remember_me' is set in localStorage on every login:
//   '1' → remember-me session → tokens live in localStorage
//   '0' → session-only        → tokens live in sessionStorage
// Checking the wrong store first is the root cause of stale timezone data:
// a previous remember-me session leaves user data in localStorage with
// timezone=null, which then shadows the correct sessionStorage user.
const getToken = (key) => {
  const remember = localStorage.getItem('wq_remember_me')
  if (remember === '0') {
    // Session-only login: prefer sessionStorage; fall back to localStorage
    // only as a safety net (e.g. during the instant before logout clears it).
    return sessionStorage.getItem(key) || localStorage.getItem(key)
  }
  // Remember-me login (or first ever login before flag is set): localStorage first.
  return localStorage.getItem(key) || sessionStorage.getItem(key)
}

// ─── shared axios helper ──────────────────────────────────────────────────────
const authAxios = axios.create()
authAxios.interceptors.request.use((cfg) => {
  const token = getToken(JWT_STORAGE_KEY)
  if (token) cfg.headers.Authorization = `Bearer ${token}`
  return cfg
})

// Shared in-flight promise for sessionLogin() so concurrent callers (app
// bootstrap + ProtectedRoute) reuse one /api/session-token/ request.
let sessionLoginPromise = null

// ─── store ────────────────────────────────────────────────────────────────────
const useAuthStore = create((set, get) => ({
  user:            getToken(USER_STORAGE_KEY) ? JSON.parse(getToken(USER_STORAGE_KEY)) : null,
  token:           getToken(JWT_STORAGE_KEY),
  isAuthenticated: !!getToken(JWT_STORAGE_KEY),
  isLoading:       false,
  error:           null,

  // Holds 2FA challenge state while waiting for the code:
  //   { temp_token, method: 'totp' | 'email', sent_to?: 'a***@x.com', resend_after?: seconds }
  pendingTwoFactor: null,

  // ── login (step 1) ──────────────────────────────────────────────────────────
  // authMethod: 'totp' (default — Authenticator app) or 'email' (send code via email)
  // remember: true → 30-day access token; false → 24-hour access token (both use localStorage)
  login: async (username, password, authMethod = 'totp', remember = false) => {
    set({ isLoading: true, error: null, pendingTwoFactor: null })
    try {
      const response = await axios.post(`${API_URL}/api/login/`, {
        username,
        password,
        auth_method: authMethod,
        remember,
      })
      const data = response.data

      // Backend signals 2FA is required — carry `remember` into pendingTwoFactor
      if (data.require_2fa) {
        set({
          isLoading: false,
          pendingTwoFactor: {
            temp_token:   data.temp_token,
            method:       data.method || 'totp',
            sent_to:      data.sent_to || null,
            resend_after: data.resend_after || 0,
            remember,
          },
        })
        return {
          success: true,
          require_2fa: true,
          method:  data.method || 'totp',
          sent_to: data.sent_to || null,
          resend_after: data.resend_after || 0,
        }
      }

      // Store tokens in localStorage (remember=true) or sessionStorage (remember=false).
      // When session-only, purge any stale localStorage entries first so that
      // getToken() doesn't accidentally read old data from a prior session.
      const { access_token, refresh_token, user } = data
      if (!remember) {
        localStorage.removeItem(JWT_STORAGE_KEY)
        localStorage.removeItem(import.meta.env.VITE_REFRESH_TOKEN_KEY)
        localStorage.removeItem(USER_STORAGE_KEY)
      }
      const tokenStore = remember ? localStorage : sessionStorage
      tokenStore.setItem(JWT_STORAGE_KEY, access_token)
      tokenStore.setItem(import.meta.env.VITE_REFRESH_TOKEN_KEY, refresh_token)
      tokenStore.setItem(USER_STORAGE_KEY, JSON.stringify(user))
      localStorage.setItem('wq_remember_me', remember ? '1' : '0')
      set({ user, token: access_token, isAuthenticated: true, isLoading: false })
      return { success: true, require_2fa: false }
    } catch (error) {
      const data    = error.response?.data
      const message = data?.error || data?.detail || (typeof data === 'string' ? data : null) || 'Login failed'
      set({ error: message, isLoading: false })
      return { success: false, error: message }
    }
  },

  // ── silent SSO from a Django session ─────────────────────────────────────────
  // When the SPA has no JWT yet, try to mint one from an existing Django session
  // (same-origin cookie) — e.g. an admin who opened a project dashboard link from
  // the admin panel. On success the user is logged in without a second prompt;
  // on failure (no session) the caller falls through to the normal login page.
  sessionLogin: async () => {
    // Single-flight: the app-bootstrap probe and ProtectedRoute can both call
    // this on a fresh tab; share one in-flight request so we don't mint twice.
    if (sessionLoginPromise) return sessionLoginPromise
    sessionLoginPromise = (async () => {
    try {
      const response = await axios.get(`${API_URL}/api/session-token/`, { withCredentials: true })
      const data = response.data
      if (!data || !data.success || !data.access_token) return { success: false }
      const { access_token, refresh_token, user } = data
      // Honour the remember-me choice from the original login (the flag lives in
      // localStorage, shared across tabs). A non-remember ('0') session must keep
      // its minted token in sessionStorage so it dies with the tab — matching the
      // browser-close Django session cookie that backed this SSO. First-ever SSO
      // with no prior flag (e.g. an admin opening a dashboard link from the admin
      // panel) is treated as remember-me so the handoff persists, as before.
      const remember = localStorage.getItem('wq_remember_me')
      const sessionOnly = remember === '0'
      if (sessionOnly) {
        localStorage.removeItem(JWT_STORAGE_KEY)
        localStorage.removeItem(import.meta.env.VITE_REFRESH_TOKEN_KEY)
        localStorage.removeItem(USER_STORAGE_KEY)
      }
      const tokenStore = sessionOnly ? sessionStorage : localStorage
      tokenStore.setItem(JWT_STORAGE_KEY, access_token)
      tokenStore.setItem(import.meta.env.VITE_REFRESH_TOKEN_KEY, refresh_token)
      tokenStore.setItem(USER_STORAGE_KEY, JSON.stringify(user))
      if (remember === null) localStorage.setItem('wq_remember_me', '1')
      set({ user, token: access_token, isAuthenticated: true })
      return { success: true }
    } catch {
      return { success: false }
    }
    })()
    try {
      return await sessionLoginPromise
    } finally {
      sessionLoginPromise = null
    }
  },

  // ── verify 2FA (step 2) ─────────────────────────────────────────────────────
  // method defaults to whatever was set in pendingTwoFactor (so callers don't
  // have to pass it twice). Pass explicitly if you need to override.
  verifyTwoFactor: async (tempToken, code, method) => {
    const state = get()
    const resolvedMethod = method || state.pendingTwoFactor?.method || 'totp'
    const remember = state.pendingTwoFactor?.remember || false
    set({ isLoading: true, error: null })
    try {
      const response = await axios.post(`${API_URL}/api/2fa/verify/`, {
        temp_token: tempToken,
        totp_code:  code,
        method:     resolvedMethod,
        remember,
      })
      const { access_token, refresh_token, user } = response.data
      if (!remember) {
        localStorage.removeItem(JWT_STORAGE_KEY)
        localStorage.removeItem(import.meta.env.VITE_REFRESH_TOKEN_KEY)
        localStorage.removeItem(USER_STORAGE_KEY)
      }
      const tokenStore = remember ? localStorage : sessionStorage
      tokenStore.setItem(JWT_STORAGE_KEY, access_token)
      tokenStore.setItem(import.meta.env.VITE_REFRESH_TOKEN_KEY, refresh_token)
      tokenStore.setItem(USER_STORAGE_KEY, JSON.stringify(user))
      localStorage.setItem('wq_remember_me', remember ? '1' : '0')
      set({ user, token: access_token, isAuthenticated: true, isLoading: false, pendingTwoFactor: null })
      return { success: true }
    } catch (error) {
      const data    = error.response?.data
      const message = data?.error || data?.detail || 'Invalid code. Please try again.'
      set({ error: message, isLoading: false })
      return { success: false, error: message, reason: data?.reason }
    }
  },

  // ── resend email 2FA code ──────────────────────────────────────────────────
  resendEmail2faCode: async (tempToken) => {
    try {
      const response = await axios.post(`${API_URL}/api/2fa/email/resend/`, {
        temp_token: tempToken,
      })
      return { success: true, message: response.data.message, resend_after: response.data.resend_after }
    } catch (error) {
      const data    = error.response?.data
      const message = data?.error || 'Could not send code. Try again later.'
      return {
        success: false,
        error: message,
        retry_after: data?.retry_after || 0,
        status: error.response?.status,
      }
    }
  },

  // ── logout ──────────────────────────────────────────────────────────────────
  logout: () => {
    // Clear from both stores — handles any tokens left in sessionStorage
    // from the brief period when session-only storage was active
    const REFRESH_KEY = import.meta.env.VITE_REFRESH_TOKEN_KEY
    ;[localStorage, sessionStorage].forEach(s => {
      s.removeItem(JWT_STORAGE_KEY)
      s.removeItem(REFRESH_KEY)
      s.removeItem(USER_STORAGE_KEY)
    })
    // Clear the remember-me flag too, so the next load's storage-selection logic
    // starts clean (matches clearAuthStorage in sessionExpiry.js).
    localStorage.removeItem('wq_remember_me')
    set({ user: null, token: null, isAuthenticated: false, pendingTwoFactor: null })
  },

  fetchCurrentUser: async () => {
    const stored = getToken(USER_STORAGE_KEY)
    if (stored) {
      const user = JSON.parse(stored)
      set({ user })
      return user
    }
    return null
  },

  updateUser: (userData) => {
    set({ user: userData })
    const remember = localStorage.getItem('wq_remember_me')
    const store = remember === '0' ? sessionStorage : localStorage
    store.setItem(USER_STORAGE_KEY, JSON.stringify(userData))
  },

  clearError: () => set({ error: null }),

  // ── 2FA account management API helpers ─────────────────────────────────────
  twoFactorApi: {
    getStatus: () =>
      authAxios.get(`${API_URL}/api/account/2fa/status/`),
    setup: (forceReset = false) =>
      authAxios.post(`${API_URL}/api/account/2fa/setup/`, { force_reset: forceReset }),
    enable: (totp_code) =>
      authAxios.post(`${API_URL}/api/account/2fa/enable/`, { totp_code }),
    disable: () =>
      authAxios.post(`${API_URL}/api/account/2fa/disable/`),
    getBackupCodes: () =>
      authAxios.get(`${API_URL}/api/account/2fa/backup-codes/`),
    setupFromToken: (token) =>
      axios.get(`${API_URL}/api/account/2fa/setup-from-token/`, { params: { token } }),
    enableFromToken: (token, totp_code) =>
      axios.post(`${API_URL}/api/account/2fa/enable-from-token/`, { token, totp_code }),
    resendSetupEmail: () =>
      authAxios.post(`${API_URL}/api/account/2fa/resend-setup-email/`),
    adminGetUserStatus: (email) =>
      authAxios.get(`${API_URL}/api/admin/2fa/user-status/`, { params: { email } }),
    adminDisableUser: (user_email) =>
      authAxios.post(`${API_URL}/api/admin/2fa/disable-user/`, { user_email }),
  },
}))

export default useAuthStore
